import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Calendar, 
  AlertTriangle, 
  DollarSign,
  Loader2,
  ChevronLeft,
  ChevronRight,
  XCircle,
  CheckCircle
} from 'lucide-react';
import blackoutDateService, { BlackoutDate } from '../../services/blackoutDateService';
import BlackoutDateModal from './BlackoutDateModal';

interface BlackoutDatesListProps {
  hotelId: number;
}

const BlackoutDatesList: React.FC<BlackoutDatesListProps> = ({ hotelId }) => {
  const [blackoutDates, setBlackoutDates] = useState<BlackoutDate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedBlackoutDate, setSelectedBlackoutDate] = useState<BlackoutDate | null>(null);

  useEffect(() => {
    loadBlackoutDates();
  }, [hotelId, pagination.page]);

  const loadBlackoutDates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await blackoutDateService.getBlackoutDates(hotelId, pagination.page, pagination.limit);
      
      if (response.success) {
        setBlackoutDates(response.data);
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            ...response.pagination!
          }));
        }
      } else {
        setError(response.message || 'Failed to load blackout dates');
      }
    } catch (err: any) {
      console.error('Error loading blackout dates:', err);
      setError(err.response?.data?.message || 'Failed to load blackout dates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedBlackoutDate(null);
    setIsModalOpen(true);
  };

  const handleEdit = (blackoutDate: BlackoutDate) => {
    setModalMode('edit');
    setSelectedBlackoutDate(blackoutDate);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this blackout date?')) {
      return;
    }

    try {
      const response = await blackoutDateService.deleteBlackoutDate(id);
      if (response.success) {
        loadBlackoutDates();
      } else {
        setError(response.message || 'Failed to delete blackout date');
      }
    } catch (err: any) {
      console.error('Error deleting blackout date:', err);
      setError(err.response?.data?.message || 'Failed to delete blackout date');
    }
  };

  const handleModalSave = () => {
    setIsModalOpen(false);
    loadBlackoutDates();
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateRange = (fromDate: string, toDate: string) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    
    if (from.toDateString() === to.toDateString()) {
      return formatDate(fromDate);
    }
    
    return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
  };

  const isPastDate = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const blackoutDate = new Date(dateString);
    return blackoutDate < today;
  };

  if (loading && blackoutDates.length === 0) {
    return (
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-300">Loading blackout dates...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            Blackout Dates
          </CardTitle>
          <Button
            onClick={handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Blackout Date
          </Button>
        </CardHeader>
        
        <CardContent>
          {error && (
            <div className="bg-red-900/20 border border-red-500/50 rounded-md p-3 mb-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            </div>
          )}

          {blackoutDates.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-300 mb-2">No Blackout Dates</h3>
              <p className="text-gray-400 mb-4">
                No blackout dates have been configured for this hotel yet.
              </p>
              <Button
                onClick={handleCreate}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Blackout Date
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {blackoutDates.map((blackoutDate) => (
                <div
                  key={blackoutDate.id}
                  className={`p-4 rounded-lg border ${
                    blackoutDate.is_hotel_unavailable
                      ? 'bg-red-900/20 border-red-500/50'
                      : 'bg-gray-800 border-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-white">
                          {formatDateRange(blackoutDate.blackout_from, blackoutDate.blackout_to)}
                        </span>
                        {isPastDate(blackoutDate.blackout_to) && (
                          <span className="text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded">
                            Past
                          </span>
                        )}
                        {blackoutDate.is_hotel_unavailable ? (
                          <div className="flex items-center space-x-1 text-red-400">
                            <XCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Hotel Unavailable</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-1 text-green-400">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Special Rate</span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-gray-300 text-sm mb-2">
                        {blackoutDate.reason}
                      </p>
                      
                      {blackoutDate.special_rate && (
                        <div className="flex items-center space-x-1 text-blue-400">
                          <DollarSign className="h-4 w-4" />
                          <span className="text-sm">
                            Special Rate: ₹{blackoutDate.special_rate}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex space-x-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(blackoutDate)}
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(blackoutDate.id!)}
                        className="border-red-600 text-red-400 hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700">
              <div className="text-sm text-gray-400">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <span className="flex items-center px-3 py-2 text-sm text-gray-300">
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.total_pages || loading}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      <BlackoutDateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleModalSave}
        hotelId={hotelId}
        blackoutDate={selectedBlackoutDate}
        mode={modalMode}
      />
    </div>
  );
};

export default BlackoutDatesList;
